import { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { useDateFilter } from "@/context/DateFilterContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Archive, LayoutGrid, TableIcon, Sparkles, Link2, Zap, Target } from "lucide-react";
import { STAGES, STAGE_COLORS } from "@/lib/constants";
import { useSettings } from "@/context/SettingsContext";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { CommentThread } from "@/components/CommentThread";
import { toast } from "sonner";

const empty = { title: "", category: "Device/Machine", brand_target: "Both", source_link: "", stage: "Discovery", linked_goal_id: null, impact_score: 3, effort_score: 3, checklist: { safety: false, roi: false, regulatory: false, pilot: false }, assigned_researcher: "", rejection_reason: null, description: "" };

export default function Pipeline() {
  const { start, end } = useDateFilter();
  const { settings } = useSettings();
  const [ideas, setIdeas] = useState([]);
  const [goals, setGoals] = useState([]);
  const [view, setView] = useState("board");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [sumBusy, setSumBusy] = useState(false);
  const [summary, setSummary] = useState("");
  const [dragId, setDragId] = useState(null);
  const [rejectFor, setRejectFor] = useState(null);

  const load = () => api.get("/ideas", { params: { start, end } }).then((r) => setIdeas(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/goals").then((r) => setGoals(r.data)).catch(() => {}); /* eslint-disable-next-line */ }, [start, end]);

  const openNew = () => { setForm(empty); setEditId(null); setSummary(""); setOpen(true); };
  const openEdit = (i) => { setForm({ ...empty, ...i }); setEditId(i.id); setSummary(""); setOpen(true); };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    try {
      if (editId) await api.put(`/ideas/${editId}`, form);
      else await api.post("/ideas", form);
      setOpen(false); load(); toast.success(editId ? "Idea updated" : "Idea added");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const archive = (id) => setRejectFor(id);
  const del = async (id) => { await api.delete(`/ideas/${id}`, { params: { permanent: true } }); load(); toast.success("Idea deleted"); };

  const moveStage = async (id, stage) => {
    if (stage === "Archived") { setRejectFor(id); return; }
    await api.patch(`/ideas/${id}/stage`, { stage });
    load();
  };

  const confirmReject = async (reason) => {
    try {
      await api.post(`/ideas/${rejectFor}/archive`, { reason });
      setRejectFor(null); load(); toast.success("Idea moved to Archived bank");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const suggestScores = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/suggest-scores", { title: form.title, category: form.category, description: form.description });
      setForm((f) => ({ ...f, impact_score: data.impact_score, effort_score: data.effort_score }));
      toast.success(`AI: ${data.rationale || "scores suggested"}`);
    } catch (e) { toast.error(apiErr(e)); }
    setAiBusy(false);
  };

  const summarize = async () => {
    if (!form.source_link) return toast.error("Add a Source/Link first");
    setSumBusy(true);
    try {
      const { data } = await api.post("/ai/summarize", { url: form.source_link });
      setSummary(data.summary);
    } catch (e) { toast.error(apiErr(e)); }
    setSumBusy(false);
  };

  const onDrop = (stage) => { if (dragId) moveStage(dragId, stage); setDragId(null); };

  const IdeaCard = ({ i }) => (
    <div draggable onDragStart={() => setDragId(i.id)} onDragEnd={() => setDragId(null)}
      onClick={() => setDetail(i)}
      className="cursor-pointer rounded-lg border border-slate-200 bg-white p-3 shadow-sm transition-transform hover:-translate-y-0.5"
      data-testid={`idea-card-${i.id}`}>
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-semibold leading-snug text-slate-900">{i.title}</p>
        <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
          <button onClick={() => openEdit(i)} className="text-slate-400 hover:text-primary" data-testid={`edit-idea-${i.id}`}><Pencil size={13} /></button>
          <button onClick={() => archive(i.id)} className="text-slate-400 hover:text-amber-600" data-testid={`archive-idea-${i.id}`}><Archive size={13} /></button>
          <ConfirmDelete onConfirm={() => del(i.id)} name={i.title} testid={`delete-idea-${i.id}`} size={13} />
        </div>
      </div>
      <p className="mt-1 text-[11px] text-muted-foreground">{i.category} · {i.brand_target}</p>
      <div className="mt-2 flex gap-2 text-[11px]">
        <span className="inline-flex items-center gap-1 rounded bg-emerald-50 px-1.5 py-0.5 text-emerald-700"><Zap size={11} /> I{i.impact_score}</span>
        <span className="inline-flex items-center gap-1 rounded bg-sky-50 px-1.5 py-0.5 text-sky-700">E{i.effort_score}</span>
        {i.assigned_researcher && <span className="truncate text-muted-foreground">{i.assigned_researcher}</span>}
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="pipeline-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-head text-3xl font-extrabold tracking-tight">R&D Pipeline</h1>
          <p className="text-muted-foreground">Scout, validate & ship emerging tech, services & formulations.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-slate-200 bg-white p-0.5">
            <button onClick={() => setView("board")} className={`rounded-md px-3 py-1.5 text-sm ${view === "board" ? "bg-primary text-white" : "text-slate-600"}`} data-testid="view-board-btn"><LayoutGrid size={15} /></button>
            <button onClick={() => setView("table")} className={`rounded-md px-3 py-1.5 text-sm ${view === "table" ? "bg-primary text-white" : "text-slate-600"}`} data-testid="view-table-btn"><TableIcon size={15} /></button>
          </div>
          <Button onClick={openNew} data-testid="new-idea-btn"><Plus size={16} className="mr-1" /> New Idea</Button>
        </div>
      </div>

      {view === "board" ? (
        <div className="flex gap-4 overflow-x-auto pb-4 kanban-scroll" data-testid="kanban-board">
          {STAGES.map((s) => {
            const items = ideas.filter((i) => i.stage === s.key);
            return (
              <div key={s.key} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(s.key)}
                className={`min-w-[270px] flex-1 rounded-xl border-t-4 bg-slate-50/60 p-3 ${STAGE_COLORS[s.key]}`}
                data-testid={`kanban-col-${s.key}`}>
                <div className="mb-3 flex items-center justify-between">
                  <span className="text-sm font-semibold text-slate-700">{s.icon} {s.label}</span>
                  <Badge variant="secondary">{items.length}</Badge>
                </div>
                <div className="space-y-2 min-h-[60px]">
                  {items.map((i) => <IdeaCard key={i.id} i={i} />)}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <Card className="border-slate-200">
          <Table data-testid="ideas-table">
            <TableHeader><TableRow>
              <TableHead>Title</TableHead><TableHead>Category</TableHead><TableHead>Brand</TableHead>
              <TableHead>Stage</TableHead><TableHead>Impact</TableHead><TableHead>Effort</TableHead><TableHead>Researcher</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {ideas.map((i) => (
                <TableRow key={i.id} className="cursor-pointer" onClick={() => setDetail(i)} data-testid={`idea-row-${i.id}`}>
                  <TableCell className="font-medium">{i.title}</TableCell>
                  <TableCell>{i.category}</TableCell><TableCell>{i.brand_target}</TableCell>
                  <TableCell><Badge variant="outline">{i.stage}</Badge></TableCell>
                  <TableCell>{i.impact_score}</TableCell><TableCell>{i.effort_score}</TableCell>
                  <TableCell>{i.assigned_researcher}</TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <div className="flex gap-1">
                      <button onClick={() => openEdit(i)} className="text-slate-400 hover:text-primary"><Pencil size={15} /></button>
                      <button onClick={() => archive(i.id)} className="text-slate-400 hover:text-amber-600"><Archive size={15} /></button>
                      <ConfirmDelete onConfirm={() => del(i.id)} name={i.title} testid={`delete-idea-row-${i.id}`} />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Create / Edit Modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="idea-modal">
          <DialogHeader><DialogTitle>{editId ? "Edit Idea" : "New R&D Idea"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Exosome Regenerative Facial" data-testid="idea-title-input" /></div>
            <div><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="idea-desc-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="idea-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{settings.idea_categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label>Brand Target</Label>
                <Select value={form.brand_target} onValueChange={(v) => setForm({ ...form, brand_target: v })}>
                  <SelectTrigger data-testid="idea-brand-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{settings.brands.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div>
              <Label>Source / Link</Label>
              <div className="flex gap-2">
                <Input value={form.source_link} onChange={(e) => setForm({ ...form, source_link: e.target.value })} placeholder="https://..." data-testid="idea-link-input" />
                <Button type="button" variant="secondary" onClick={summarize} disabled={sumBusy} data-testid="ai-summarize-btn"><Sparkles size={14} className="mr-1" />{sumBusy ? "..." : "Summarize"}</Button>
              </div>
              {summary && <div className="mt-2 rounded-lg bg-accent p-3 text-xs text-slate-700 whitespace-pre-wrap" data-testid="ai-summary-box">{summary}</div>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Stage</Label>
                <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v })}>
                  <SelectTrigger data-testid="idea-stage-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{STAGES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label>Linked Goal</Label>
                <Select value={form.linked_goal_id || "none"} onValueChange={(v) => setForm({ ...form, linked_goal_id: v === "none" ? null : v })}>
                  <SelectTrigger data-testid="idea-goal-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="none">None</SelectItem>{goals.map((g) => <SelectItem key={g.id} value={g.id}>{g.title.slice(0, 30)}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div className="rounded-lg border border-slate-200 p-3">
              <div className="mb-2 flex items-center justify-between">
                <Label>Impact & Feasibility Scores</Label>
                <Button type="button" size="sm" variant="outline" onClick={suggestScores} disabled={aiBusy} data-testid="ai-suggest-scores-btn"><Sparkles size={13} className="mr-1" />{aiBusy ? "Thinking..." : "AI Suggest"}</Button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Impact ({form.impact_score})</Label>
                  <Select value={String(form.impact_score)} onValueChange={(v) => setForm({ ...form, impact_score: Number(v) })}>
                    <SelectTrigger data-testid="idea-impact-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{[1, 2, 3, 4, 5].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><Label className="text-xs">Effort ({form.effort_score})</Label>
                  <Select value={String(form.effort_score)} onValueChange={(v) => setForm({ ...form, effort_score: Number(v) })}>
                    <SelectTrigger data-testid="idea-effort-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{[1, 2, 3, 4, 5].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
                  </Select></div>
              </div>
            </div>
            <div>
              <Label>Validation Checklist</Label>
              <div className="mt-1 grid grid-cols-2 gap-2 text-sm">
                {[["safety", "Safety Check"], ["roi", "ROI / Cost Analysis"], ["regulatory", "Regulatory Approval"], ["pilot", "Pilot Feasibility"]].map(([k, l]) => (
                  <label key={k} className="flex items-center gap-2">
                    <Checkbox checked={form.checklist[k]} onCheckedChange={(c) => setForm({ ...form, checklist: { ...form.checklist, [k]: !!c } })} data-testid={`checklist-${k}`} /> {l}
                  </label>
                ))}
              </div>
            </div>
            <div><Label>Assigned Researcher</Label>
              <Select value={form.assigned_researcher || "none"} onValueChange={(v) => setForm({ ...form, assigned_researcher: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="idea-researcher-select"><SelectValue placeholder="Select researcher" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Unassigned</SelectItem>{Array.from(new Set([...(settings.researchers || []), form.assigned_researcher].filter(Boolean))).map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-idea-btn">{editId ? "Save Changes" : "Add Idea"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rejection reason */}
      <Dialog open={!!rejectFor} onOpenChange={(o) => !o && setRejectFor(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="reject-modal">
          <DialogHeader><DialogTitle>Reason for Archiving</DialogTitle></DialogHeader>
          <div className="grid gap-2">
            {settings.rejection_reasons.map((r) => (
              <Button key={r} variant="outline" onClick={() => confirmReject(r)} data-testid={`reject-${r.replace(/\W+/g, "-")}`}>{r}</Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail Drawer */}
      <Drawer open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DrawerContent className="max-h-[88vh]" data-testid="idea-detail-drawer">
          {detail && (
            <div className="mx-auto w-full max-w-2xl overflow-y-auto p-6">
              <DrawerHeader className="px-0"><DrawerTitle className="font-head text-2xl">{detail.title}</DrawerTitle></DrawerHeader>
              <div className="flex flex-wrap gap-2 text-xs">
                <Badge variant="outline">{detail.category}</Badge>
                <Badge variant="outline">{detail.brand_target}</Badge>
                <Badge className="bg-primary text-white">{detail.stage}</Badge>
              </div>
              {detail.description && <p className="mt-3 text-sm text-slate-700">{detail.description}</p>}
              <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                <div className="rounded-lg bg-emerald-50 p-3"><Zap size={14} className="text-emerald-600" /><p className="mt-1 font-semibold">Impact {detail.impact_score}/5</p></div>
                <div className="rounded-lg bg-sky-50 p-3"><Target size={14} className="text-sky-600" /><p className="mt-1 font-semibold">Effort {detail.effort_score}/5</p></div>
              </div>
              {detail.source_link && <a href={detail.source_link} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1 text-sm text-secondary hover:underline"><Link2 size={14} /> Source</a>}
              <div className="mt-6 border-t border-slate-200 pt-4">
                <h4 className="mb-3 font-head font-bold">Review & Rationale</h4>
                <CommentThread entityType="idea" entityId={detail.id} />
              </div>
            </div>
          )}
        </DrawerContent>
      </Drawer>
    </div>
  );
}
